import Phaser from 'phaser';
import { Behavior, BehaviorConfig } from './Behavior';
import { Entity } from '../base/Entity';
import { FacingState } from '../base/Unit';

export type FlipMode = 'velocity' | 'target' | 'manual';

export interface FlipConfig extends BehaviorConfig {
  mode?: FlipMode;           // How to determine direction
  target?: Entity;           // For mode='target', which entity to look at
  flipOnStart?: boolean;     // If starts flipped
  enabled?: boolean;         // If auto-flip is active
  invert?: boolean;          // If true, invert flip logic
}

/**
 * FlipBehavior - Automatic sprite flipping based on movement or target
 *
 * Used by: Cow, Soldier, Bat, Human, etc.
 *
 * Modes:
 * - 'velocity': Flip based on velocity.x direction (for patrolling/flying entities)
 * - 'target': Flip to always face a target entity (for enemies tracking player)
 * - 'manual': No automatic flip, only manual API (setFlipped, lookAt)
 *
 * Features:
 * - Synchronizes with Unit's facing state system
 * - Can change mode at runtime
 * - Manual override API
 */
export class FlipBehavior extends Behavior {
  public mode: FlipMode;
  public target?: Entity;
  private invert: boolean;

  constructor(entity: Entity, config: FlipConfig = {}) {
    super(entity, config);
    this.mode = config.mode ?? 'velocity';
    this.target = config.target;
    this.enabled = config.enabled ?? true;
    this.invert = config.invert ?? false;

    if (config.flipOnStart) {
      this.entity.flipX = true;
      this.syncFacingState(true);
    }
  }

  /**
   * Update flip state based on current mode
   */
  update(time: number, delta: number): void {
    if (!this.enabled) return;

    switch (this.mode) {
      case 'velocity':
        this.flipByVelocity();
        break;
      case 'target':
        this.flipTowardTarget();
        break;
      // 'manual' - no automatic action
    }
  }

  /**
   * Flip based on velocity X (for Walker/Flyer)
   */
  private flipByVelocity(): void {
    const body = this.entity.body as Phaser.Physics.Arcade.Body;
    if (!body || body.velocity.x === 0) return;

    const shouldFlip = this.invert ? body.velocity.x > 0 : body.velocity.x < 0;
    this.applyFlip(shouldFlip);
  }

  /**
   * Flip toward a target (for enemies looking at player)
   */
  private flipTowardTarget(): void {
    if (!this.target || !this.target.active) return;

    const baseFlip = this.target.x < this.entity.x;
    const shouldFlip = this.invert ? !baseFlip : baseFlip;
    this.applyFlip(shouldFlip);
  }

  /**
   * Apply flip and synchronize with facing state
   */
  private applyFlip(shouldFlip: boolean): void {
    if (this.entity.flipX === shouldFlip) return;

    this.entity.flipX = shouldFlip;
    this.syncFacingState(shouldFlip);
  }

  /**
   * Synchronize flipX with Unit's facing state system
   */
  private syncFacingState(flipped: boolean): void {
    // Check if entity has setFacing method (Unit class)
    if (typeof (this.entity as Entity & { setFacing?: (state: FacingState) => void }).setFacing === 'function') {
      const facing: FacingState = flipped ? 'left' : 'right';
      // Use direct assignment to avoid triggering flipX again
      const unit = this.entity as Entity & { states: { facing: FacingState } };
      if (unit.states) {
        unit.states.facing = facing;
        this.entity.emit('facingChanged', facing);
      }
    }
  }

  // ========================================
  // Public API
  // ========================================

  /**
   * Manually set flip state
   */
  setFlipped(flipped: boolean): void {
    this.applyFlip(flipped);
  }

  /**
   * Flip to look at a specific entity (one-time)
   */
  lookAt(target: Entity): void {
    if (!target || !target.active) return;
    const shouldFlip = target.x < this.entity.x;
    this.applyFlip(shouldFlip);
  }

  /**
   * Set the target entity for target mode
   */
  setTarget(target: Entity | undefined): void {
    this.target = target;
  }

  /**
   * Change flip mode at runtime
   */
  setMode(mode: FlipMode): void {
    this.mode = mode;
  }

  /**
   * Check if currently flipped (facing left)
   */
  isFlipped(): boolean {
    return this.entity.flipX;
  }

  /**
   * Get current facing direction
   */
  getFacing(): FacingState {
    return this.entity.flipX ? 'left' : 'right';
  }
}
